import { unwrapScriptValue } from '@/common/utils/wrapScriptValue'

interface TypedValue {
  value: any
  type: string
}

/** избегаем цикла UniData → setType */
const UNIDATA_TO_PLAIN = Symbol.for('workflow.UniData.toPlain')

export function registerUniDataToPlain(target: object, toPlain: () => Record<string, any>): void {
  ;(target as any)[UNIDATA_TO_PLAIN] = toPlain
}

function isRegisteredUniData(value: unknown): boolean {
  if (value == null || typeof value !== 'object') return false
  const fn = (value as Record<PropertyKey, unknown>)[UNIDATA_TO_PLAIN as unknown as PropertyKey]
  return typeof fn === 'function'
}

function tryGetUniDataPlain(value: unknown): Record<string, any> | null {
  if (!isRegisteredUniData(value)) return null
  const fn = (value as Record<PropertyKey, unknown>)[UNIDATA_TO_PLAIN as unknown as PropertyKey] as () => Record<
    string,
    any
  >
  return fn()
}

/** Совпадает с ожиданием ru.esoft.workflow.jackson.UniDataDeserializer / UniDataTableFieldsDeserializer */
function hasJacksonUniDataFieldsShape(v: unknown): boolean {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return false
  const root = v as Record<string, unknown>
  const f = root.fields
  if (f == null || typeof f !== 'object' || Array.isArray(f)) return false
  const inner = (f as Record<string, unknown>).fields
  return inner != null && typeof inner === 'object' && !Array.isArray(inner)
}

function normalizeUniDataTypedFieldsMap(map: Record<string, any>): Record<string, any> {
  const out: Record<string, any> = {}
  for (const [k, tv] of Object.entries(map)) {
    if (
      tv &&
      typeof tv === 'object' &&
      tv.type === 'UniData' &&
      tv.value != null &&
      typeof tv.value === 'object' &&
      !Array.isArray(tv.value)
    ) {
      out[k] = { type: 'UniData', value: ensureUniDataValueForJackson(tv.value) }
    } else {
      out[k] = tv
    }
  }
  return out
}

/**
 * ru.esoft.workflow.jackson.UniClientDataDeserializer ожидает openPage, doActionId, params (и get("params").fields()).
 * Частичный clientData из строк дерева / бэка приводит к NPE при DnD (currentData → UniData → clientData).
 */
function normalizeNestedUniClientDataForJackson(raw: Record<string, unknown>): Record<string, unknown> {
  const params = raw.params
  const doActionId = raw.doActionId
  const openPage = raw.openPage
  return {
    elementConditions: raw.elementConditions ?? null,
    reloadActions: raw.reloadActions ?? null,
    params:
      params != null && typeof params === 'object' && !Array.isArray(params)
        ? (params as Record<string, unknown>)
        : {},
    doActionId:
      typeof doActionId === 'number'
        ? doActionId
        : typeof doActionId === 'string' && doActionId.trim() !== ''
          ? Number(doActionId) || 0
          : 0,
    openPage:
      typeof openPage === 'number'
        ? openPage
        : typeof openPage === 'string' && openPage.trim() !== ''
          ? Number(openPage) || 0
          : 0,
  }
}

function scrubUniDataClientDataEnvelope(envelope: Record<string, any>): Record<string, any> {
  if (!('clientData' in envelope)) {
    return envelope
  }
  const raw = envelope.clientData
  if (raw == null || typeof raw !== 'object' || Array.isArray(raw)) {
    const { clientData: _omit, ...rest } = envelope
    return rest
  }
  return {
    ...envelope,
    clientData: normalizeNestedUniClientDataForJackson(raw as Record<string, unknown>),
  }
}

/**
 * Преобразует внутреннюю карту UniData или значение поля type=UniData в JSON-форму,
 * которую сервер разбирает через UniDataDeserializer (обязателен узел fields.fields).
 */
export function ensureUniDataValueForJackson(value: any): any {
  if (value == null || typeof value !== 'object' || Array.isArray(value)) {
    return value
  }
  if (hasJacksonUniDataFieldsShape(value)) {
    const inner = (value as Record<string, any>).fields.fields as Record<string, any>
    const normalized = normalizeUniDataTypedFieldsMap(inner)
    return scrubUniDataClientDataEnvelope({
      ...value,
      fields: {
        ...(value as Record<string, any>).fields,
        fields: normalized,
      },
    })
  }
  return { fields: { fields: normalizeUniDataTypedFieldsMap(value as Record<string, any>) } }
}

/**
 * Документ-строка (плоская или `fields.*`) → TypedValue для clientData на сервер (executeProcedure и т.д.).
 * Нужно для DnD TreeTable: `source` / `dest` без обёртки { type, value } иначе отбрасываются в createSafeClientData.
 */
export function toClientExchangeUniDataDocumentField(row: unknown): TypedValue {
  if (row == null || typeof row !== 'object' || Array.isArray(row)) {
    return setType(row)
  }
  const r = row as Record<string, any>
  if (
    typeof r.type === 'string' &&
    'value' in r &&
    Object.keys(r).every((k) => ['type', 'value', '_attributes', '_text'].includes(k))
  ) {
    if (r.type === 'UniData') {
      return { type: 'UniData', value: ensureUniDataValueForJackson(r.value) }
    }
    return r as TypedValue
  }
  let inner: Record<string, any>
  if (r.fields && typeof r.fields === 'object' && !Array.isArray(r.fields)) {
    inner = Object.fromEntries(
      Object.entries(r.fields).map(([k, v]) => [
        k,
        v && typeof v === 'object' && 'type' in v && 'value' in v ? v : setType(v),
      ]),
    )
  } else {
    inner = Object.fromEntries(
      Object.entries(r)
        .filter(([k]) => k !== 'attributes' && k !== 'errors')
        .map(([k, v]) => [
          k,
          v && typeof v === 'object' && 'type' in v && 'value' in v ? v : setType(v),
        ]),
    )
  }
  return { type: 'UniData', value: ensureUniDataValueForJackson(inner) }
}

const typeMap: Record<string, string> = {
  number: 'Integer',
  string: 'String',
  boolean: 'Boolean',
}

function isUniDataTableInnerShape(v: unknown): boolean {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return false
  return Array.isArray((v as Record<string, unknown>).rows)
}

export const typedValueSerializer = (value: any): string => {
  if (value === null || value === undefined) {
    return 'String'
  }
  if (Array.isArray(value)) {
    return 'Array'
  }
  if (typeof value === 'object') {
    if (isRegisteredUniData(value)) {
      return 'UniData'
    }
    if (isUniDataTableInnerShape(value)) {
      return 'UniDataTable'
    }
    return 'Object'
  }
  return typeMap[typeof value] || 'String'
}

/**
 * Устанавливает типы для всех свойств value
 */
export const setTypes = (value: Record<string, any>): Record<string, TypedValue> => {
  const result: Record<string, TypedValue> = {}

  Object.keys(value).forEach(key => {
    const entry = value[key]
    if (entry === null || entry === undefined) {
      result[key] = setType(entry)
      return
    }
    if (!entry.type) {
      if (typeof entry === 'string' && entry.match('GMT')) {
        result[key] = { value: entry, type: 'Date' }
      } else if (entry instanceof Date) {
        result[key] = { value: entry.toUTCString(), type: 'Date' }
      } else {
        result[key] = { value: entry, type: typedValueSerializer(entry) }
      }
    } else {
      result[key] = entry
    }
  })

  return result
}

const DATE_TYPED_STORE_TYPES = new Set(['Date', 'Timestamp', 'TimezonelessDate'])

function epochMillisToIsoUtc(ms: number): string | null {
  if (!Number.isFinite(ms)) return null
  const date = new Date(ms)
  if (Number.isNaN(date.getTime())) return null
  return date.toISOString().replace(/Z$/, '+00:00')
}

function normalizeDateTypedStoreValue(type: string, value: unknown): unknown {
  if (!DATE_TYPED_STORE_TYPES.has(type)) return value
  if (typeof value !== 'number') return value
  return epochMillisToIsoUtc(value) ?? value
}

/**
 * Определяет тип и возвращает объект {type: type, value: value}
 */
export const setType = (value: any): TypedValue => {
  if (value !== null && value !== undefined) {
    if (value.type) {
      const unwrappedValue = unwrapScriptValue(value.value)
      const dateWire = normalizeDateTypedStoreValue(String(value.type), unwrappedValue)
      if (dateWire !== value.value) {
        return { ...value, value: dateWire }
      }
      return value
    }

    const unwrapped = unwrapScriptValue(value)

    if (typeof unwrapped === 'string' && unwrapped.match('GMT')) {
      return { value: unwrapped, type: 'Date' }
    } else if (unwrapped instanceof Date) {
      return { value: unwrapped.toUTCString(), type: 'Date' }
    } else {
      const uniPlain = tryGetUniDataPlain(unwrapped)
      if (uniPlain !== null) {
        return { type: 'UniData', value: ensureUniDataValueForJackson(uniPlain) }
      }
      return { value: unwrapped, type: typedValueSerializer(unwrapped) }
    }
  }
  return { value, type: 'String' }
}
